import { CheckCircle2, Rocket, TrendingUp, BarChart3, Globe2 } from 'lucide-react';

const PHASES = [
  {
    n: 1, Icon: CheckCircle2, days: 'DAYS 1-7', title: 'Onboarding & Evaluation',
    bullets: ['Product assessment', 'Market fit analysis', 'Pricing strategy', 'Documentation setup'],
  },
  {
    n: 2, Icon: Rocket, days: 'DAYS 8-21', title: 'Initial Market Launch',
    bullets: ['Inventory arrival', 'App listing live', 'Soft launch testing', 'First sales tracking'],
  },
  {
    n: 3, Icon: TrendingUp, days: 'DAYS 22-45', title: 'Marketing & Retail',
    bullets: ['Retail store placement', 'Social media campaigns', 'Consumer promotions', 'Visibility push'],
  },
  {
    n: 4, Icon: BarChart3, days: 'DAYS 46-70', title: 'Data & Optimization',
    bullets: ['Sales analysis review', 'Regional insights', 'Price adjustments', 'SKU optimization'],
  },
  {
    n: 5, Icon: Globe2, days: 'DAYS 71-90', title: 'Scale Across ANZ',
    bullets: ['Expansion planning', 'New territory rollout', 'Increased inventory', 'Growth acceleration'],
    highlight: true,
  },
];

const DesktopPhase = ({ phase }) => {
  const { n, Icon, days, title, bullets, highlight } = phase;
  return (
    <div className="relative">
      <div className="flex flex-col items-center">
        <div className={`h-16 w-16 ${highlight ? 'bg-gradient-to-br from-[#004aad] to-[#0066cc]' : 'bg-[#004aad]'} rounded-full flex items-center justify-center text-white font-bold text-xl mb-4 relative z-10 shadow-lg`}>
          {n}
        </div>
        <div className={highlight
          ? 'bg-gradient-to-br from-[#004aad] to-[#0066cc] rounded-xl p-6 shadow-xl border-2 border-[#004aad]'
          : 'bg-white rounded-xl p-6 shadow-lg border-2 border-gray-100 hover:border-[#004aad] transition-all'}
        >
          <div className="text-center mb-4">
            <div className={`inline-flex items-center justify-center h-12 w-12 ${highlight ? 'bg-white/20 backdrop-blur-sm' : 'bg-blue-50'} rounded-lg mb-3`}>
              <Icon className={`h-6 w-6 ${highlight ? 'text-white' : 'text-[#004aad]'}`} />
            </div>
            <div className={`text-sm font-semibold ${highlight ? 'text-white' : 'text-[#004aad]'} mb-2`}>{days}</div>
            <h3 className={`text-lg font-bold ${highlight ? 'text-white' : 'text-gray-900'} mb-2`}>{title}</h3>
          </div>
          <ul className={`space-y-2 text-sm ${highlight ? 'text-white' : 'text-gray-600'}`}>
            {bullets.map((b) => <li key={b}>• {b}</li>)}
          </ul>
        </div>
      </div>
    </div>
  );
};

const MobilePhase = ({ phase }) => {
  const { n, days, title, bullets, highlight } = phase;
  return (
    <div className="relative pl-12">
      {!highlight && <div className="absolute left-0 top-0 h-full w-0.5 bg-blue-200"></div>}
      <div className={`absolute left-0 top-0 h-10 w-10 ${highlight ? 'bg-gradient-to-br from-[#004aad] to-[#0066cc]' : 'bg-[#004aad]'} rounded-full flex items-center justify-center text-white font-bold`}>
        {n}
      </div>
      <div className={highlight
        ? 'bg-gradient-to-br from-[#004aad] to-[#0066cc] rounded-xl p-6 shadow-xl'
        : 'bg-white rounded-xl p-6 shadow-lg border-2 border-gray-100'}
      >
        <div className={`text-sm font-semibold ${highlight ? 'text-white' : 'text-[#004aad]'} mb-2`}>{days}</div>
        <h3 className={`text-xl font-bold ${highlight ? 'text-white' : 'text-gray-900'} mb-3`}>{title}</h3>
        <ul className={`space-y-2 ${highlight ? 'text-white' : 'text-gray-600'}`}>
          {bullets.map((b) => <li key={b}>• {b}</li>)}
        </ul>
      </div>
    </div>
  );
};

export const PartnerJourney = () => (
  <section className="py-20 md:py-32 bg-white">
    <div className="container mx-auto px-6">
      <div className="text-center mb-16">
        <div className="inline-flex items-center space-x-2 bg-blue-50 text-[#004aad] px-5 py-2 rounded-full text-sm font-semibold mb-6">
          <Rocket className="h-4 w-4" />
          <span>YOUR PARTNER JOURNEY</span>
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Your First 90 Days With Atlas</h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          A clear, systematic roadmap from onboarding to scaling across ANZ. Know exactly what to expect at every stage.
        </p>
      </div>

      <div className="max-w-7xl mx-auto">
        <div className="hidden lg:block relative">
          <div className="absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-[#004aad] to-blue-200"></div>
          <div className="grid grid-cols-5 gap-4">
            {PHASES.map((p) => <DesktopPhase key={p.n} phase={p} />)}
          </div>
        </div>

        <div className="lg:hidden space-y-6">
          {PHASES.map((p) => <MobilePhase key={p.n} phase={p} />)}
        </div>

        <div className="mt-12 text-center bg-blue-50 rounded-2xl p-8">
          <p className="text-lg text-gray-700 mb-4">
            <strong className="text-[#004aad]">Every step is guided by our experienced team.</strong> You'll have dedicated support, weekly check-ins, and transparent communication throughout your journey.
          </p>
          <div className="flex items-center justify-center space-x-8 text-sm text-gray-600">
            {['Weekly progress reports', 'Dedicated account manager', '24/7 support access'].map((t) => (
              <div key={t} className="flex items-center space-x-2">
                <CheckCircle2 className="h-5 w-5 text-[#004aad]" />
                <span>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);
